import { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';

import { routes } from './utils/routes';
import Layout from './components/Layout';
import LandingPage from './Pages/LandingPage';
import ProfilePage from './Pages/ProfilePage';
import BookingsPage from './Pages/BookingsPage';
import BenefitsPage from './Pages/BenefitsPage';
import QuotasPage from './Pages/QuotasPage';
import OrderHistory from './Pages/OrderHistory';
import SkiDaysPage from './Pages/SkiDaysPage';
import OwnedRoomsPage from './Pages/OwnedRoomsPage';
import ITicketRedirect from './Pages/ITicketRedirect';
import Spinner from './components/Spinner';
import { getDomain, fetchCustomerData } from './utils/functions';
import { Customer, CustomerData } from './types/customer';

function App(): JSX.Element {
  const domain = getDomain();

  const isAuthenticated = useIsAuthenticated();
  const [customer, setCustomer] = useState<Customer>();
  const [cdpCustomer, setCdpCustomer] = useState<CustomerData>();
  const { inProgress, instance, accounts } = useMsal();

  const [idToken, setIdToken] = useState<string>('');
  const [isOwner, setIsOwner] = useState(true);
  const { i18n } = useTranslation();

  useEffect(() => {
    async function getProfileData() {
      try {
        const response = await instance.acquireTokenSilent({
          account: accounts[0],
          scopes: ['openid'],
        });
        setIdToken(response.idToken);

        const owner = accounts[0]?.idTokenClaims?.jobTitle === 'owner';
        setIsOwner(owner);

        const customerData = await fetchCustomerData(response.idToken, true);
        if (customerData?.customer) {
          setCustomer(customerData.customer);
        }
      } catch (e) {
        // Session has likely expired, user is not available.
        // Just logs out user from the app, but doesn't try to
        // log them out from azure.
        if (accounts.length === 0) {
          instance.logoutRedirect({
            onRedirectNavigate: () => {
              return false;
            },
          });
        } else {
          // Just log out the user if the session
          // can't be authenticated.
          const logoutRequest = {
            account: instance.getAccountByHomeId(accounts[0].homeAccountId),
            postLogoutRedirectUri: `/${i18n.language}`,
          };
          instance.logoutRedirect(logoutRequest);
        }
      }
    }

    if (isAuthenticated && inProgress === InteractionStatus.None) {
      getProfileData();
    }
  }, [isAuthenticated, inProgress]);

  // When initial customer data is fetched, and idToken has been set, refetch customer
  // data from CDP to check for loyalty/Tekso Id. This can be passed as a prop to
  // all components that need to show the loyalty card modal.
  useEffect(() => {
    async function getCDPCustomer() {
      const response = await fetchCustomerData(idToken, false, 'cdp');
      if (response && response.cdpData) {
        setCdpCustomer(response);
      }
    }

    if (idToken) {
      getCDPCustomer();
    }
  }, [idToken]);

  // Handle logout page
  const LogoutWrapper = () => {
    const { instance, accounts } = useMsal();

    useEffect(() => {
      async function logout() {
        const domains: { [key: string]: string } = {
          ruka: '.ruka.fi',
          pyha: '.pyha.fi',
        };
        Cookies.remove('Drupal.visitor.id_token');
        Cookies.remove('logged_in', { path: '/', domain: domains[domain] });
        Cookies.remove('drupal_logged_in', {
          path: '',
          domain: domains[domain],
        });
        Cookies.remove('login_redirect', { path: '', domain: domains[domain] });

        const drupalSite: { [key: string]: string } = {
          ruka: `${process.env.REACT_APP_DRUPAL_BASE_URL_RUKA}/${i18n.language}`,
          pyha: `${process.env.REACT_APP_DRUPAL_BASE_URL_PYHA}/${i18n.language}`,
        };
        const drupalUrl = drupalSite[domain];

        const currentUrl = new URL(window.location.href);
        // This indicates which version of login app (ruka or pyhä) triggered the logout.
        const logoutDestination =
          currentUrl.searchParams.get('logout_destination');
        // This will be the final redirect url after user has been logged out of the login app sites,
        // likely an external site.
        const redirectAfterLogout = currentUrl.searchParams.get('redirect_to');
        let redirectUrl = logoutDestination ?? drupalUrl;

        if (logoutDestination) {
          const loginApps: { [key: string]: string } = {
            ruka:
              process.env.REACT_APP_LOGIN_APP_BASE_URL_RUKA ??
              'https://login-staging.ruka.fi',
            pyha:
              process.env.REACT_APP_LOGIN_APP_BASE_URL_PYHA ??
              'https://login-staging.pyha.fi',
          };

          // Logout from drupal.
          try {
            await fetch(`${drupalUrl}/user/logout`, {
              credentials: 'include',
              mode: 'no-cors',
            });
          } catch (error) {
            console.log(error);
          }

          if (window.location.origin.includes('localhost')) {
            // Redirect to an external destination.
            if (redirectAfterLogout) {
              redirectUrl = redirectAfterLogout;
              // On local, no need to do any login redirects.
            } else {
              redirectUrl = `/${i18n.language}`;
            }
          } else if (!logoutDestination.includes(domain)) {
            // Redirect to an external destination.
            if (redirectAfterLogout) {
              redirectUrl = redirectAfterLogout;
              // User should be logged out from everywhere, redirect to the site
              // where logout was initiated.
            } else {
              redirectUrl = `${logoutDestination}/${i18n.language}`;
            }
          } else {
            // Redirect to the other login site to log out the user.
            for (const [key, value] of Object.entries(loginApps)) {
              if (!domain.includes(key)) {
                redirectUrl = `${value}/logout?logout_destination=${logoutDestination}`;
                if (redirectAfterLogout) {
                  redirectUrl += `&redirect_to=${redirectAfterLogout}`;
                }
              }
            }
          }
        } else if (!logoutDestination && redirectAfterLogout) {
          redirectUrl = redirectAfterLogout;
        }

        if (accounts && accounts.length > 0) {
          const logoutRequest = {
            account: instance.getAccountByHomeId(accounts[0].homeAccountId),
            postLogoutRedirectUri: redirectUrl,
          };
          instance.logoutRedirect(logoutRequest);
        } else {
          window.location.replace(redirectUrl);
        }
      }

      if (inProgress === InteractionStatus.None) {
        logout();
      }
    }, [inProgress]);

    return <></>;
  };

  return (
    <div className={`App site-${domain} h-full bg-gray-200`}>
      <Routes>
        <Route path='logout' element={<LogoutWrapper />} />
        <Route
          path=':lang/'
          element={<Layout customer={customer} cdpCustomer={cdpCustomer} idToken={idToken}/>}
        >
          {/* Show when authentication is in progress. */}
          {inProgress !== InteractionStatus.None && (
            <Route path='*' element={<Spinner />} />
          )}

          {inProgress === InteractionStatus.None && (
            <>
              {isAuthenticated && (
                <>
                  <Route
                    index
                    element={
                      <ProfilePage
                        customer={customer}
                        cdpCustomer={cdpCustomer}
                        idToken={idToken}
                      />
                    }
                  />
                  {/* When logging in, the redirect url is /login */}
                  <Route
                    path={routes.BOOKINGS[i18n.language]}
                    element={<BookingsPage idToken={idToken} />}
                  />
                  <Route
                    path={routes.BENEFITS[i18n.language]}
                    element={<BenefitsPage idToken={idToken} />}
                  />
                  <Route
                    path={routes.ORDER_HISTORY[i18n.language]}
                    element={
                      <OrderHistory
                        email={accounts[0].username}
                        idToken={idToken}
                      />
                    }
                  />
                  <Route
                    path={routes.SKIDAYS[i18n.language]}
                    element={<SkiDaysPage idToken={idToken} />}
                  />
                  <Route
                    path={routes.ITICKET_REDIRECT[i18n.language]}
                    element={<ITicketRedirect idToken={idToken} />}
                  />
                  {isOwner && (
                    <>
                      <Route
                        path={routes.QUOTAS[i18n.language]}
                        element={
                          <QuotasPage
                            idToken={idToken}
                            email={accounts[0].username}
                          />
                        }
                      />
                      ,
                      <Route
                        path={routes.ROOMS[i18n.language]}
                        element={<OwnedRoomsPage idToken={idToken} />}
                      />
                    </>
                  )}
                </>
              )}

              {!isAuthenticated && <Route index element={<LandingPage />} />}
            </>
          )}
        </Route>
        {/* Redirect to front page if the route isn't found. */}
        <Route
          path='*'
          element={<Navigate replace to={`/${i18n.language}`} />}
        />
      </Routes>
      <footer className='bg-gray-500 h-max min-h-[14rem]'></footer>
    </div>
  );
}

export default App;
